import type { ShipmentDestination } from '@/api/shipmentsApi'
import { formatPostalAddress, type PostalAddress } from '@/lib/address/address-rules'
import type { CreatedProduct } from '@/types/products'
import { escapeHtml } from '@/lib/printLabel'

/**
 * Pure rules for shipping product to a partner (z8nrz7eaex), kept out of the
 * components so they are unit-tested (vitest here runs `.ts` only).
 */

/** Where a product is, in one phrase: a slot path, a partner code, or nowhere. */
export function productLocation(p: CreatedProduct): { kind: 'stored' | 'shipped' | 'none'; text: string } {
  if (p.locationPartner) return { kind: 'shipped', text: p.locationPartner.code }
  if (p.storagePath) return { kind: 'stored', text: p.storagePath }
  return { kind: 'none', text: '' }
}

/** A product can be shipped unless it already has been. */
export function isShippable(p: CreatedProduct): boolean {
  return !p.locationPartnerId && !p.locationPartner
}

/**
 * Products matching `q` on DIN, product code, patient name, MRN or location —
 * the storage path or, for a shipped product, the partner code.
 * Case-insensitive; blank matches all.
 */
/**
 * `locate` is how the screen writes a storage location (`useLocationText`):
 * with it, a search for the room or building a product is in finds it too, not
 * only one for the path inside the unit.
 */
export function filterProductsForShipping(
  products: readonly CreatedProduct[],
  q: string,
  locate?: (path: string) => string,
): CreatedProduct[] {
  const needle = q.trim().toLowerCase()
  if (!needle) return [...products]
  return products.filter(p =>
    [
      p.din,
      p.productDescriptionCode,
      p.patientName,
      p.patientMrn,
      p.storagePath,
      p.storagePath && locate ? locate(p.storagePath) : null,
      p.locationPartner?.code,
    ].some(v => v?.toLowerCase().includes(needle)),
  )
}

// ─── Address ──────────────────────────────────────────────────────────────────

/**
 * Countries whose addresses read `City, ST ZIP`. Country is free text on a
 * partner (z8nrz7earp), so the common spellings are all accepted. Everything
 * else — Belgium and the rest of the EU included — reads `ZIP City`, the
 * European standard and the safer default for anywhere not listed.
 */
const US_NAMES = new Set(['us', 'usa', 'u.s.', 'u.s.a.', 'united states', 'united states of america'])

export function isUsAddress(a: Partial<ShipmentDestination>): boolean {
  return US_NAMES.has(a.country?.trim().toLowerCase() ?? '')
}

/**
 * An address as the lines it prints in, in the local order for its country:
 *
 *   US                           EU
 *   123 Main Street, Suite 400   Kempische Steenweg 303/200
 *   Tampa, FL 33602              3500 Hasselt
 *   United States                Belgium
 *
 * A shipment's own printed lines win, then the structured address laid out by
 * its country's rules (z8nrz7epeq). Only an address from before structured
 * addresses falls back to guessing the layout from the country's name. Blank
 * parts are dropped rather than printed as gaps or dangling commas.
 */
export function formatAddressLines(
  a: Partial<ShipmentDestination> & { postalAddress?: PostalAddress | null },
): string[] {
  if (a.lines?.length) return a.lines
  const structured = a.postalAddress ?? a.postal
  if (structured?.countryCode) return formatPostalAddress(structured)

  const street = a.address?.trim() ?? ''
  const city = a.city?.trim() ?? ''
  const state = a.state?.trim() ?? ''
  const zip = a.zipCode?.trim() ?? ''
  const country = a.country?.trim() ?? ''

  const locality = isUsAddress(a)
    ? [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    : [[zip, city].filter(Boolean).join(' '), state].filter(Boolean).join(', ')

  return [street, locality, country].filter(Boolean)
}

/** A partner has enough address to put on a shipping label. */
export function hasShippableAddress(a: Partial<ShipmentDestination>): boolean {
  return !!(a.address?.trim() && a.city?.trim() && a.country?.trim())
}

// ─── Shipping label ───────────────────────────────────────────────────────────

export interface ShippingLabelInput {
  /** Set once the shipment exists; a label printed before Confirm has none. */
  shipmentNumber?: string | null
  partner: { code: string; name: string }
  /** The address to print — the partner's live one, or a shipment's snapshot. */
  destination: Partial<ShipmentDestination>
  courier?: { code: string; name: string } | null
  trackingNumber?: string | null
  comments?: string | null
  products: { din: string; productCode: string; volumeMl?: number | null }[]
  date: Date
}

/** Styles for the label document — a 4×6 in shipping label. */
export const SHIPPING_LABEL_CSS = `
  @page { size: 4in 6in; margin: 0.2in; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #000; margin: 0; }
  .lbl { border: 2px solid #000; padding: 8px; }
  .row { display: flex; justify-content: space-between; gap: 8px; }
  .h { font-size: 10px; font-weight: bold; text-transform: uppercase; letter-spacing: .04em; color: #333; }
  .to { font-size: 15px; line-height: 1.3; margin: 4px 0 8px; }
  .to .name { font-weight: bold; }
  .num { font-size: 16px; font-weight: bold; }
  hr { border: 0; border-top: 1px solid #000; margin: 6px 0; }
  table { width: 100%; border-collapse: collapse; }
  td, th { text-align: left; padding: 2px 4px; border-bottom: 1px solid #ccc; font-size: 10px; }
  .muted { color: #444; }
`

/**
 * The shipping label as an HTML fragment for `printLabelViaIframe`: SHIP TO
 * (the address in its country's order), the courier and tracking reference,
 * what is in the box, and the comments. Every value is escaped.
 */
export function buildShippingLabelHtml(input: ShippingLabelInput): string {
  const e = (v: string | number | null | undefined) => escapeHtml(v == null ? '' : String(v))
  const address = formatAddressLines(input.destination)
  const rows = input.products
    .map(
      p =>
        `<tr><td>${e(p.din)}</td><td>${e(p.productCode)}</td><td>${p.volumeMl == null ? '' : `${e(p.volumeMl)} mL`}</td></tr>`,
    )
    .join('')

  return [
    '<div class="lbl">',
    '<div class="row">',
    `<div><div class="h">Shipment</div><div class="num">${input.shipmentNumber ? e(input.shipmentNumber) : 'PENDING'}</div></div>`,
    `<div style="text-align:right"><div class="h">Ship date</div><div>${e(input.date.toLocaleDateString())}</div></div>`,
    '</div><hr/>',
    '<div class="h">Ship to</div>',
    `<div class="to"><div class="name">${e(input.partner.name)} (${e(input.partner.code)})</div>`,
    ...address.map(line => `<div>${e(line)}</div>`),
    '</div>',
    input.courier || input.trackingNumber
      ? `<div class="row"><div><div class="h">Courier</div><div>${input.courier ? `${e(input.courier.name)} (${e(input.courier.code)})` : '—'}</div></div>` +
        `<div style="text-align:right"><div class="h">Tracking</div><div>${e(input.trackingNumber) || '—'}</div></div></div><hr/>`
      : '<hr/>',
    `<div class="h">Contents (${input.products.length})</div>`,
    `<table><thead><tr><th>DIN</th><th>Product code</th><th>Volume</th></tr></thead><tbody>${rows}</tbody></table>`,
    input.comments ? `<hr/><div class="h">Comments</div><div class="muted">${e(input.comments)}</div>` : '',
    '</div>',
  ].join('')
}
